import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Table, Alert, Spinner,
    Modal, InputGroup
} from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency } from "../utils/formatters";
import { TVA_TAUX, CSS_TAUX } from "../utils/constantes";
import './Factures.css';

export default function NouvelleFacture() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [loadingDevis, setLoadingDevis] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [clients, setClients] = useState([]);
    const [produits, setProduits] = useState([]);
    const [showProduitModal, setShowProduitModal] = useState(false);
    
    // États pour la numérotation
    const [numerotation, setNumerotation] = useState(null);
    const [numeroFacture, setNumeroFacture] = useState('');
    
    // Récupérer les paramètres de l'URL
    const queryParams = new URLSearchParams(location.search);
    const devisId = queryParams.get('devis');
    const clientId = queryParams.get('client'); // Récupérer l'ID du client depuis l'URL
    
    // État du formulaire
    const [facture, setFacture] = useState({
        id_client: clientId || '', // Pré-remplir avec l'ID du client si présent
        date_facture: new Date().toISOString().split('T')[0],
        date_echeance: '',
        remise_globale: 0,
        avance_reglement: 0,
        notes: '',
        lignes: []
    });

    useEffect(() => {
        fetchClients();
        fetchProduits();
        fetchNumerotation();
        fetchTaxes();
    
        // Si un ID de devis est présent, charger le devis
        if (devisId) {
            chargerDevis(devisId);
        }
    }, [devisId]);

    // Charger la configuration de numérotation
    const [taxes, setTaxes] = useState({
        tva_taux: 18,
        css_taux: 1,
        tva_active: true,
        css_active: true
    });

    // Charger les taxes
    const fetchTaxes = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/parametres.php?action=taxes',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) {
                    setTaxes(result.data);
                }
            }
        } catch (err) {
            console.error("Erreur chargement taxes", err);
        }
    };
  
    const fetchNumerotation = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/factures.php?action=prochain_numero',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                const data = result.data;
                setNumerotation(data.config);
                
                let numero = data.config.facture_prefix;
                if (data.config.facture_annee) numero += new Date().getFullYear();
                if (data.config.facture_mois) numero += ('0' + (new Date().getMonth() + 1)).slice(-2);
                numero += data.prochain.toString().padStart(data.config.facture_chiffres, '0');
                
                setNumeroFacture(numero);
            }
        } catch (err) {
            console.error("Erreur", err);
        }
    };

    const chargerDevis = async (id) => {
        try {
            setLoadingDevis(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/devis.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!response.ok) throw new Error("Erreur chargement du devis");
            
            const result = await response.json();
            const devisData = result.data;
            
            if (devisData) {
                // Pré-remplir la facture avec les données du devis
                setFacture({
                    id_client: devisData.id_client,
                    date_facture: new Date().toISOString().split('T')[0],
                    date_echeance: devisData.date_echeance || '',
                    remise_globale: devisData.remise_globale || 0,
                    avance_reglement: 0,
                    notes: `Facture issue du devis ${devisData.numero_proforma}\n${devisData.notes || ''}`,
                    lignes: devisData.lignes?.map(ligne => ({
                        id_produit: ligne.id_produit,
                        reference: ligne.reference,
                        designation: ligne.designation,
                        quantite: ligne.quantite,
                        prix_unitaire_ht: ligne.prix_unitaire_ht,
                        tva_taux: ligne.tva_taux || TVA_TAUX,
                        css_taux: ligne.css_taux || CSS_TAUX,
                        remise: ligne.remise || 0
                    })) || []
                });
                
                console.log("Devis chargé avec succès:", devisData);
            }
        } catch (err) {
            console.error("Erreur chargement devis:", err);
            setError("Erreur lors du chargement du devis");
        } finally {
            setLoadingDevis(false);
        }
    };

    const fetchClients = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/clients.php',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setClients(result.data || []);
                
                // Si un clientId est présent, vérifier qu'il existe dans la liste
                if (clientId && result.data) {
                    const clientExists = result.data.some(c => c.id_client == clientId);
                    if (!clientExists) {
                        setError("Le client spécifié n'existe pas");
                    }
                }
            }
        } catch (err) {
            console.error("Erreur chargement clients", err);
        }
    };

    const fetchProduits = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/produits.php?include_stock=true',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                // S'assurer que stock_reel est défini
                const produitsAvecStock = (result.data || []).map(p => ({
                    ...p,
                    stock_reel: p.stock_reel || 0,
                    seuil_alerte: p.seuil_alerte || 5
                }));
                setProduits(produitsAvecStock);
            }
        } catch (err) {
            console.error("Erreur chargement produits", err);
        }
    };

    const ajouterLigne = (produit) => {
        const nouvelleLigne = {
            id_produit: produit.id_produit,
            reference: produit.reference,
            designation: produit.designation,
            quantite: 1,
            prix_unitaire_ht: produit.prix_vente_ht,
            tva_taux: taxes.tva_active ? taxes.tva_taux : 0,
            css_taux: taxes.css_active ? taxes.css_taux : 0,
            remise: 0
        };
        setFacture({
            ...facture,
            lignes: [...facture.lignes, nouvelleLigne]
        });
        setShowProduitModal(false);
    };

    const supprimerLigne = (index) => {
        const nouvellesLignes = facture.lignes.filter((_, i) => i !== index);
        setFacture({ ...facture, lignes: nouvellesLignes });
    };

    const updateLigne = (index, champ, valeur) => {
        const nouvellesLignes = [...facture.lignes];
        nouvellesLignes[index][champ] = valeur;
        setFacture({ ...facture, lignes: nouvellesLignes });
    };

    // Calcul des totaux
    const calculerTotaux = () => {
        let total_ht = 0;
        let total_tva = 0;
        let total_css = 0;

        facture.lignes.forEach(ligne => {
            const montant_ligne_ht = ligne.quantite * ligne.prix_unitaire_ht * (1 - (ligne.remise || 0) / 100);
            total_ht += montant_ligne_ht;
            
            // Utiliser les taux actifs
            if (taxes.tva_active) {
                total_tva += montant_ligne_ht * (ligne.tva_taux || taxes.tva_taux) / 100;
            }
            if (taxes.css_active) {
                total_css += montant_ligne_ht * (ligne.css_taux || taxes.css_taux) / 100;
            }
        });

        const total_ttc = total_ht + total_tva + total_css;
        const net_a_payer = total_ttc - (facture.remise_globale || 0) - (facture.avance_reglement || 0);

        return { total_ht, total_tva, total_css, total_ttc, net_a_payer };
    };

    const totaux = calculerTotaux();

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!facture.id_client) {
            setError("Veuillez sélectionner un client");
            return;
        }
        
        if (facture.lignes.length === 0) {
            setError("Veuillez ajouter au moins un produit");
            return;
        }

        setSaving(true);
        setError("");

        try {
            const userStr = localStorage.getItem("user");
            if (!userStr) {
                setError("Utilisateur non connecté");
                return;
            }

            const user = JSON.parse(userStr);
            const id_utilisateur = user.id || user.id_utilisateur;
            
            if (!id_utilisateur) {
                setError("ID utilisateur non trouvé");
                return;
            }

            const token = localStorage.getItem("token");
            
            const payload = {
                action: 'create',
                ...facture,
                numero_facture: numeroFacture, // Ajout du numéro généré
                id_utilisateur: id_utilisateur
            };
            
            // Si on vient d'un devis, on ajoute l'info
            if (devisId) {
                payload.id_proforma = devisId;
            }

            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/factures.php',
                {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                }
            );

            const result = await response.json();

            if (result.success) {
                setSuccess("Facture créée avec succès !");
                setTimeout(() => {
                    navigate('/ventes/factures/liste');
                }, 2000);
            } else {
                setError(result.message || "Erreur lors de la création");
            }
        } catch (err) {
            console.error("Erreur:", err);
            setError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    // Indicateur de chargement du devis
    if (loadingDevis) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <Spinner animation="border" variant="primary" />
                        <span className="ms-3">Chargement du devis...</span>
                    </div>
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <ol className="breadcrumb fs-sm mb-1">
                                        <li className="breadcrumb-item"><Link to="/dashboard">Tableau de bord</Link></li>
                                        <li className="breadcrumb-item"><Link to="/ventes">Ventes</Link></li>
                                        <li className="breadcrumb-item"><Link to="/ventes/factures/liste">Factures</Link></li>
                                        <li className="breadcrumb-item active">
                                            {devisId ? 'Convertir devis en facture' : 'Nouvelle facture'}
                                        </li>
                                    </ol>
                                    <h4 className="main-title mb-0">
                                        <i className="ri-file-list-3-line text-primary me-2"></i>
                                        {devisId ? 'Convertir devis en facture' : 'Créer une facture'}
                                    </h4>
                                    {devisId && (
                                        <p className="text-muted mt-2">
                                            Les données du devis ont été pré-remplies. Vous pouvez les modifier avant de créer la facture.
                                        </p>
                                    )}
                                    {clientId && !devisId && (
                                        <p className="text-muted mt-2">
                                            Client pré-sélectionné. Vous pouvez modifier si nécessaire.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {error && (
                        <Alert variant="danger" onClose={() => setError("")} dismissible>
                            <i className="ri-error-warning-line me-2"></i>
                            {error}
                        </Alert>
                    )}
                    
                    {success && (
                        <Alert variant="success">
                            <i className="ri-checkbox-circle-line me-2"></i>
                            {success}
                        </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                        <Row>
                            {/* Informations générales */}
                            <Col lg={8}>
                                <Card className="border-0 shadow-sm mb-4">
                                    <Card.Header className="bg-primary text-white">
                                        <h5 className="mb-0">
                                            <i className="ri-information-line me-2"></i>
                                            Informations générales
                                        </h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Numéro de facture</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={numeroFacture}
                                                        disabled
                                                        readOnly
                                                        className="bg-light"
                                                    />
                                                    <Form.Text className="text-muted">
                                                        Généré automatiquement selon la configuration
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Client <span className="text-danger">*</span></Form.Label>
                                                    <Form.Select
                                                        value={facture.id_client}
                                                        onChange={(e) => setFacture({...facture, id_client: e.target.value})}
                                                        required
                                                    >
                                                        <option value="">Sélectionner un client</option>
                                                        {clients.map(client => (
                                                            <option key={client.id_client} value={client.id_client}>
                                                                {client.raison_sociale} ({client.code_client})
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Date facture</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={facture.date_facture}
                                                        onChange={(e) => setFacture({...facture, date_facture: e.target.value})}
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Date échéance</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={facture.date_echeance}
                                                        onChange={(e) => setFacture({...facture, date_echeance: e.target.value})}
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label>Notes</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        value={facture.notes}
                                                        onChange={(e) => setFacture({...facture, notes: e.target.value})}
                                                        placeholder="Notes ou observations..."
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                {/* Lignes de facture */}
                                <Card className="border-0 shadow-sm">
                                    <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0">
                                            <i className="ri-shopping-cart-line me-2"></i>
                                            Produits / Services
                                        </h5>
                                        <Button 
                                            variant="light" 
                                            size="sm"
                                            onClick={() => setShowProduitModal(true)}
                                        >
                                            <i className="ri-add-line me-1"></i>
                                            Ajouter
                                        </Button>
                                    </Card.Header>
                                    <Card.Body>
                                        {facture.lignes.length > 0 ? (
                                            <div className="table-responsive">
                                                <Table className="align-middle">
                                                    <thead className="bg-light">
                                                        <tr>
                                                            <th>Produit</th>
                                                            <th className="text-center">Qté</th>
                                                            <th className="text-end">Prix HT</th>
                                                            <th className="text-center">Remise %</th>
                                                            <th className="text-end">Total HT</th>
                                                            <th className="text-center">TVA</th>
                                                            <th className="text-center"></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {facture.lignes.map((ligne, index) => (
                                                            <tr key={index}>
                                                                <td>
                                                                    <div className="fw-semibold">{ligne.designation}</div>
                                                                    <small className="text-muted">{ligne.reference}</small>
                                                                </td>
                                                                <td className="text-center" style={{ width: '100px' }}>
                                                                    <Form.Control
                                                                        type="number"
                                                                        min="1"
                                                                        value={ligne.quantite}
                                                                        onChange={(e) => updateLigne(index, 'quantite', parseInt(e.target.value) || 1)}
                                                                        size="sm"
                                                                    />
                                                                </td>
                                                                <td className="text-end" style={{ width: '120px' }}>
                                                                    <Form.Control
                                                                        type="number"
                                                                        step="0.01"
                                                                        value={ligne.prix_unitaire_ht}
                                                                        onChange={(e) => updateLigne(index, 'prix_unitaire_ht', parseFloat(e.target.value) || 0)}
                                                                        size="sm"
                                                                        className="text-end"
                                                                    />
                                                                </td>
                                                                <td className="text-center" style={{ width: '100px' }}>
                                                                    <Form.Control
                                                                        type="number"
                                                                        min="0"
                                                                        max="100"
                                                                        value={ligne.remise || 0}
                                                                        onChange={(e) => updateLigne(index, 'remise', parseFloat(e.target.value) || 0)}
                                                                        size="sm"
                                                                    />
                                                                </td>
                                                                <td className="text-end fw-bold">
                                                                    {formatCurrency(ligne.quantite * ligne.prix_unitaire_ht * (1 - (ligne.remise || 0)/100))}
                                                                </td>
                                                                <td className="text-center text-warning">
                                                                    {ligne.tva_taux || (taxes.tva_active ? taxes.tva_taux : 0)}%
                                                                </td>
                                                                <td className="text-center">
                                                                    <Button 
                                                                        variant="link" 
                                                                        className="text-danger p-0"
                                                                        onClick={() => supprimerLigne(index)}
                                                                    >
                                                                        <i className="ri-delete-bin-line"></i>
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        ) : (
                                            <div className="text-center py-5">
                                                <i className="ri-shopping-cart-line fs-48 text-muted mb-3"></i>
                                                <p className="text-muted">Aucun produit ajouté</p>
                                                <Button 
                                                    variant="outline-primary"
                                                    onClick={() => setShowProduitModal(true)}
                                                >
                                                    <i className="ri-add-line me-2"></i>
                                                    Ajouter un produit
                                                </Button>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Récapitulatif */}
                            <Col lg={4}>
                                <Card className="border-0 shadow-sm sticky-top" style={{ top: '20px' }}>
                                    <Card.Header className="bg-primary text-white">
                                        <h5 className="mb-0">
                                            <i className="ri-calculator-line me-2"></i>
                                            Récapitulatif
                                        </h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="recap-item d-flex justify-content-between mb-2">
                                            <span>Total HT</span>
                                            <span className="fw-bold">{formatCurrency(totaux.total_ht)}</span>
                                        </div>
                                        <div className="recap-item d-flex justify-content-between mb-2">
                                            <span>TVA {taxes.tva_active ? `(${taxes.tva_taux}%)` : '(inactive)'}</span>
                                            <span className="fw-bold text-warning">{formatCurrency(totaux.total_tva)}</span>
                                        </div>
                                        <div className="recap-item d-flex justify-content-between mb-2">
                                            <span>CSS {taxes.css_active ? `(${taxes.css_taux}%)` : '(inactive)'}</span>
                                            <span className="fw-bold text-info">{formatCurrency(totaux.total_css)}</span>
                                        </div>
                                        <hr />
                                        <div className="recap-item d-flex justify-content-between mb-3">
                                            <span className="fw-bold">Total TTC</span>
                                            <span className="fw-bold text-primary fs-5">{formatCurrency(totaux.total_ttc)}</span>
                                        </div>

                                        <div className="bg-light p-3 rounded mb-3">
                                            <Form.Group className="mb-3">
                                                <Form.Label>Remise globale (FCFA)</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    value={facture.remise_globale}
                                                    onChange={(e) => setFacture({...facture, remise_globale: parseFloat(e.target.value) || 0})}
                                                />
                                            </Form.Group>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Avance / Acompte</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    value={facture.avance_reglement}
                                                    onChange={(e) => setFacture({...facture, avance_reglement: parseFloat(e.target.value) || 0})}
                                                />
                                            </Form.Group>
                                        </div>

                                        <hr />
                                        <div className="recap-item d-flex justify-content-between mb-4">
                                            <span className="h5">Net à payer</span>
                                            <span className="h5 text-success">{formatCurrency(totaux.net_a_payer)}</span>
                                        </div>

                                        <div className="d-grid gap-2">
                                            <Button 
                                                type="submit" 
                                                variant="primary" 
                                                size="lg"
                                                disabled={saving}
                                            >
                                                {saving ? (
                                                    <>
                                                        <Spinner size="sm" className="me-2" />
                                                        Création...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="ri-save-line me-2"></i>
                                                        {devisId ? 'Créer la facture depuis le devis' : 'Créer la facture'}
                                                    </>
                                                )}
                                            </Button>
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => navigate('/ventes/factures/liste')}
                                            >
                                                Annuler
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Form>
                </div>
                <Footer />
            </div>

            {/* Modal de sélection des produits */}
            <Modal show={showProduitModal} onHide={() => setShowProduitModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Sélectionner un produit</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="table-responsive">
                        <Table hover className="align-middle">
                            <thead>
                                <tr>
                                    <th>Référence</th>
                                    <th>Désignation</th>
                                    <th className="text-end">Prix HT</th>
                                    <th className="text-center">Stock</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {produits.map(produit => (
                                    <tr key={produit.id_produit}>
                                        <td><span className="badge bg-light text-dark">{produit.reference}</span></td>
                                        <td>{produit.designation}</td>
                                        <td className="text-end">{formatCurrency(produit.prix_vente_ht)}</td>
                                        <td className="text-center">
                                            <span className={produit.stock_reel < produit.seuil_alerte ? 'text-danger fw-bold' : ''}>
                                                {produit.stock_reel || 0}
                                            </span>
                                        </td>
                                        <td>
                                            <Button 
                                                size="sm" 
                                                variant="primary"
                                                onClick={() => ajouterLigne(produit)}
                                            >
                                                <i className="ri-add-line"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
            </Modal>

            <style>{`
                .recap-item {
                    font-size: 1.1rem;
                }
                .sticky-top {
                    position: sticky;
                    top: 20px;
                    z-index: 100;
                }
            `}</style>
        </React.Fragment>
    );
}